import { useState } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/dahhtaboh653ivfvnvv0";

type Status = "idle" | "loading" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function NewsletterSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const raw = new FormData(form);

    const trap = String(raw.get("website_alt") ?? "").trim();
    if (trap) {
      setStatus("success");
      setMessage("订阅成功，我们每周一期的深扒精选会发送到你的邮箱。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    raw.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setStatus("error");
        setMessage(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage("订阅成功，我们每周一期的深扒精选会发送到你的邮箱。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="mx-auto max-w-content scroll-mt-24 px-4 pb-4 md:px-6">
      <div className="overflow-hidden rounded-lg bg-primary-950">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="p-6 md:p-10 lg:col-span-6">
            <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-accent-400">
              <span className="h-1 w-6 bg-accent-500"></span>
              WEEKLY DIGEST
            </p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
              每周一期的深扒精选
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200">
              订阅后你会收到本周最值得细读的三篇内容，以及一条独家线索的进展更新。不发推广，随时可退订。
            </p>
            <ul className="mt-6 space-y-3">
              {["每周三晚间发送", "只推内容，不含任何广告", "涉及人物一律匿名处理"].map((text) => (
                <li key={text} className="flex items-center gap-2 text-sm text-background-200">
                  <span className="flex h-4 w-4 items-center justify-center text-accent-400">
                    <i className="ri-check-line"></i>
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-primary-800 bg-foreground-950 p-6 md:p-10 lg:col-span-6 lg:border-l lg:border-t-0">
            <form onSubmit={handleSubmit} data-readdy-form>
              <label htmlFor="subscribe-email" className="block text-sm font-medium text-background-100">
                接收推送的邮箱
              </label>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-secondary-800 bg-secondary-950 px-4 py-3 text-sm text-background-50 outline-none transition-colors placeholder:text-background-500 focus:border-accent-500"
                />
                <input
                  className="field-secret-trap"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "loading" ? "提交中…" : "立即订阅"}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              <p className="mt-3 text-xs text-background-400">
                我们只会将你的邮箱用于发送内容推送，不会对外提供。
              </p>

              {status === "success" && (
                <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-900 px-4 py-3 text-xs text-background-100">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-accent-400">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-900 px-4 py-3 text-xs text-background-100">
                  <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-200">
                    <i className="ri-error-warning-line"></i>
                  </span>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}